/**
 * Stats - rendering statistics tracking (draw calls, geometries, materials, primitives).
 */

import type { IGenesysRenderer } from 'src/render/IGenesysRenderer';
import type * as THREE from 'three';

/** Statistic types tracked by the stats manager */
export enum StatsType {
  DrawCalls = 'Draw Calls',
  Triangles = 'Triangles',
  Points = 'Points',
  Lines = 'Lines',
}

export class StatsManager {
  public enabled: boolean = false;

  private stats: Map<string, number> = new Map();
  private geometries: Set<THREE.BufferGeometry> = new Set();
  private materials: Set<THREE.Material> = new Set();

  private static instance: StatsManager | null = null;
  public static getInstance(): StatsManager {
    StatsManager.instance ??= new StatsManager();
    return StatsManager.instance;
  }

  public resetStats(): void {
    for (const stat of this.stats.keys()) {
      this.stats.set(stat, 0);
    }
  }

  public incrementStat(type: string, amount: number = 1): void {
    this.stats.set(type, this.getStat(type) + amount);
  }

  public decrementStat(type: string, amount: number = 1): void {
    this.stats.set(type, this.getStat(type) - amount);
  }

  public setStat(type: string, value: number): void {
    this.stats.set(type, value);
  }

  public getStat(type: string): number {
    return this.stats.get(type) ?? 0;
  }

  public getAllStats(): Map<string, number> {
    return new Map(this.stats);
  }

  public updateRenderStats(renderer: IGenesysRenderer) {
    const webgl = renderer.asWebGL();
    const webgpu = renderer.asWebGPU();
    if (webgl) {
      const info = webgl.info;
      info.autoReset = false;
      this.setStat(StatsType.DrawCalls, info.render.calls);
      this.setStat(StatsType.Triangles, info.render.triangles);
      this.setStat(StatsType.Points, info.render.points);
      this.setStat(StatsType.Lines, info.render.lines);
      info.reset();
    }

    if (webgpu) {
      const info = webgpu.info;
      info.autoReset = false;
      this.setStat(StatsType.DrawCalls, info.render.drawCalls);
      this.setStat(StatsType.Triangles, info.render.triangles);
      this.setStat(StatsType.Points, info.render.points);
      this.setStat(StatsType.Lines, info.render.lines);
      info.reset();
    }
  }
}

export const Stats = StatsManager.getInstance();

export class StatsDisplay {
  private statsOverlay: HTMLElement | null = null;
  private gameContainer: HTMLElement;

  constructor(gameContainer: HTMLElement) {
    this.gameContainer = gameContainer;
  }

  /**
   * Removes the overlay and disables render stat collection.
   */
  public dispose(): void {
    if (this.statsOverlay) {
      this.statsOverlay.remove();
      this.statsOverlay = null;
    }
    Stats.enabled = false;
  }

  public toggle(): void {
    if (this.statsOverlay) {
      this.statsOverlay.remove();
      this.statsOverlay = null;
    } else {
      // Create stats overlay
      this.statsOverlay = document.createElement('div');
      this.statsOverlay.style.cssText = `
      position: absolute;
      top: 20px;
      right: 20px;
      width: 300px;
      background: #1a1a1a;
      color: #e0e0e0;
      border-radius: 8px;
      padding: 16px;
      font-family: monospace;
      font-size: 14px;
      z-index: 1000;
    `;

      // Add to game container
      this.gameContainer.appendChild(this.statsOverlay);
    }
    Stats.enabled = !!this.statsOverlay;
  }

  private updateStatsOverlay(): void {
    if (!this.statsOverlay) {
      return;
    }

    const allStats = Stats.getAllStats();

    if (allStats.size === 0) {
      return;
    }

    // Generate simple HTML content
    const statsList = Array.from(allStats.entries())
      .map(([name, value]) => `<div style="margin: 4px 0;"><span style="color: #ff9800;">${name}:</span> <span style="color: white;">${value}</span></div>`)
      .join('');

    // Update the stats content - target the container after the header
    this.statsOverlay.innerHTML = `
    <div style="display: flex; align-items: center; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #444;">
      <div style="width: 16px; height: 16px; margin-right: 8px; background: linear-gradient(45deg, #ff69b4, #87ceeb, #90ee90); border-radius: 2px;"></div>
      <div style="color: #4caf50; font-weight: bold;">Stats</div>
    </div>
    ${statsList}
    `;
  }

  /**
   * Updates the stats overlay content with current stats
   * Called by the world tick
   */
  public tick(): void {
    this.updateStatsOverlay();
    Stats.resetStats();
  }
}
